import React, { useState, useEffect } from 'react';
import { formatearMoneda, limpiarMoneda } from '../utils/formatMoney';
import useResponsive from '../utils/useResponsive';
import { esMexicana } from '../utils/calculoRfcCurp';
import { creditoExcedeInmueble, MENSAJE_CREDITO_INMUEBLE } from '../utils/creditoVsInmueble';

const gridCols = (isMobile, isTablet, desktopCols) => (isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : desktopCols);

const st = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #d4242a', paddingBottom: 8 },
  subtitle: { fontSize: 15, fontWeight: '600', color: '#374151', marginTop: 16, marginBottom: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  inputReadonly: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', backgroundColor: '#f3f4f6', color: '#6b7280' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 },
  moneyInput: { width: '100%', height: '42px', padding: '10px 12px 10px 24px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  saveBtn: { backgroundColor: '#1a3a6e', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 15, fontWeight: '600', cursor: 'pointer', marginTop: 8 },
  medRow: { display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 },
  medLabel: { flex: 1, fontSize: 13, color: '#374151' },
  sinoWrap: { display: 'flex', gap: 8, alignItems: 'center' },
  ppeBox: { background: '#fff8f0', border: '1px solid #fcd34d', borderRadius: 6, padding: 16, marginTop: 12 },
};

const SCOTIA_ESTADO_CIVIL_MAP = {
  'Soltero': 'Soltero', 'Casado': 'Casado',
  // Divorciado, Viudo, Union Libre y Sociedad de Convivencia no existen en el
  // catálogo de Scotiabank (solo Soltero/Casado/Otro Especifique) — se mapean a
  // "Otro Especifique" y se escribe la etiqueta original en el campo Especifique.
  'Divorciado': 'Otro Especifique', 'Viudo': 'Otro Especifique',
  'Union Libre': 'Otro Especifique', 'Sociedad de Convivencia': 'Otro Especifique',
};
const SCOTIA_REGIMEN_MAP = {
  'Sociedad Conyugal': 'Sociedad Conyugal',
  'Separación de Bienes': 'Separación Bienes',
};
const SCOTIA_TIPO_VIVIENDA_MAP = {
  'Propia': 'Propia', 'Hipotecada': 'Hipotecada', 'Rentada': 'Rentada',
  'Padres': 'Familiares', 'Otro Parentesco': 'Familiares', 'Otro': 'Otro Especifique',
};
function idPorNombreScotia(catalogo, nombre) {
  if (!nombre) return '';
  const match = catalogo.find(c => c.nombre === nombre);
  return match ? match.id : '';
}

const Sel = ({ label, value, onChange, options, placeholder = '— Selecciona —' }) => (
  <div style={st.field}>
    {label && <label style={st.label}>{label}</label>}
    <select
      value={value !== null && value !== undefined ? String(value) : ''}
      onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
      style={st.input}
    >
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.id} value={String(o.id)}>{o.nombre}</option>)}
    </select>
  </div>
);

const Txt = ({ label, value, onChange, placeholder = '', maxLength }) => (
  <div style={st.field}>
    {label && <label style={st.label}>{label}</label>}
    <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} style={st.input} />
  </div>
);

const FechaInput = ({ label, value, onChange }) => (
  <div style={st.field}>
    {label && <label style={st.label}>{label}</label>}
    <input type="date" value={value || ''} onChange={e => onChange(e.target.value || null)} style={st.input} />
  </div>
);

const Money = ({ label, value, onChange, decimales = 2 }) => {
  const [enfocado, setEnfocado] = useState(false);
  return (
    <div style={st.field}>
      {label && <label style={st.label}>{label}</label>}
      <div style={st.moneyWrap}>
        <span style={st.moneySign}>$</span>
        <input type="text" inputMode="decimal"
          value={enfocado ? (value ?? '') : formatearMoneda(value, decimales)}
          onFocus={() => setEnfocado(true)}
          onBlur={() => setEnfocado(false)}
          onChange={e => onChange(limpiarMoneda(e.target.value, decimales))}
          style={st.moneyInput} placeholder={decimales === 0 ? '0' : '0.00'} />
      </div>
    </div>
  );
};

const SiNo = ({ value, onChange, name, defaultNo }) => {
  useEffect(() => {
    if (defaultNo && (value === undefined || value === null)) onChange(false);
  }, [value, defaultNo, onChange]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = (value === undefined || value === null) && defaultNo ? false : value;
  return (
    <div style={st.sinoWrap}>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={name} checked={val === true} onChange={() => onChange(true)} /> Sí
      </label>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={name} checked={val === false} onChange={() => onChange(false)} /> No
      </label>
    </div>
  );
};

const MedRow = ({ num, label, campo, sc, set, defaultNo }) => (
  <div style={st.medRow}>
    <div style={st.medLabel}><strong>{num}.</strong> {label}</div>
    <SiNo name={`med_${campo}`} value={sc[campo]} onChange={v => set(campo, v)} defaultNo={defaultNo} />
  </div>
);

const PPESeccion = ({ prefix, sc, set }) => {
  const { isMobile, isTablet } = useResponsive();
  const grid3 = { display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, 'repeat(3, 1fr)'), gap: 16, marginBottom: 16 };
  const grid2 = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16, marginBottom: 16 };
  return (
    <>
      <div style={grid3}>
        <Txt label="Cargo Público" value={sc[`${prefix}_cargo`]} onChange={v => set(`${prefix}_cargo`, v)} />
        <Txt label="Descripción de la Función" value={sc[`${prefix}_descripcion`]} onChange={v => set(`${prefix}_descripcion`, v)} />
        <Txt label="Período" value={sc[`${prefix}_periodo`]} onChange={v => set(`${prefix}_periodo`, v)} />
      </div>
      <div style={grid2}>
        <Txt label="País" value={sc[`${prefix}_pais`]} onChange={v => set(`${prefix}_pais`, v)} />
        <Txt label="Dependencia" value={sc[`${prefix}_dependencia`]} onChange={v => set(`${prefix}_dependencia`, v)} />
      </div>
    </>
  );
};

const InfoGeneral = ({ prefix, label, sc, set, cats, conGrupoVulnerabilidad = false, numDependientes }) => {
  const { isMobile, isTablet } = useResponsive();
  const grid3 = { display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, 'repeat(3, 1fr)'), gap: 16, marginBottom: 16 };
  const N = (field) => `${prefix}_${field}`;
  const escolar = cats.escolaridades.find(e => e.id === sc[N('escolaridad_id')]);
  const ecivil  = cats.estadosCiviles.find(e => e.id === sc[N('estado_civil_id')]);
  const tViv    = cats.tiposVivienda.find(e => e.id === sc[N('tipo_vivienda_id')]);
  const esOtroEsc  = escolar && escolar.nombre.toLowerCase().includes('otro');
  const esOtroEciv = ecivil  && ecivil.nombre.toLowerCase().includes('otro');
  const esOtroTViv = tViv    && tViv.nombre.toLowerCase().includes('otro');
  const esCasado   = ecivil  && ecivil.nombre === 'Casado';

  return (
    <div style={st.card}>
      <div style={st.title}>Información General del {label}</div>

      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '0.5fr 0.5fr 0.5fr 0.65fr 1.85fr'), gap: 16, marginBottom: 16 }}>
        <div>
          <label style={st.label}>Nacionalidad</label>
          <input readOnly value={cats.nacionalidades.find(n => n.id === sc[N('nacionalidad_id')])?.nombre || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
        <div>
          <Sel label="Escolaridad" value={sc[N('escolaridad_id')]} onChange={v => set(N('escolaridad_id'), v)} options={cats.escolaridades} />
          {esOtroEsc && <Txt label="Especifique" value={sc[N('escolaridad_otro')]} onChange={v => set(N('escolaridad_otro'), v)} />}
        </div>
        <div>
          <label style={st.label}>Estado Civil</label>
          <input readOnly value={ecivil?.nombre || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
          {esOtroEciv && (
            <div style={{ marginTop: 8 }}>
              <label style={st.label}>Especifique</label>
              <input readOnly value={sc[N('estado_civil_otro')] || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
            </div>
          )}
        </div>
        {esCasado && (
          <div>
            <label style={st.label}>Régimen Matrimonial</label>
            <input readOnly value={cats.regimenMatrimonial.find(r => r.id === sc[N('regimen_matrimonial_id')])?.nombre || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1fr 1fr 1fr 0.7fr 0.6fr'), gap: 16, marginBottom: 16 }}>
        <div style={st.field}>
          <label style={st.label}>No. Identificación / Clave INE</label>
          <input readOnly value={sc[N('no_identificacion')] || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
        <div style={st.field}>
          <label style={st.label}>Tipo de Identificación</label>
          <input readOnly value={sc[N('tipo_identificacion')] || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
        <Txt label="País Emisor" value={sc[N('pais_emisor')]} onChange={v => set(N('pais_emisor'), v)} />
        <div style={st.field}>
          <label style={st.label}>Vigencia Identificación</label>
          <input readOnly type="date" value={sc[N('vigencia_identificacion')] || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
      </div>

      <div style={grid3}>
        <div>
          <label style={st.label}>Tipo de Vivienda</label>
          <input readOnly value={tViv?.nombre || ''} style={st.inputReadonly} title="Se captura en la pestaña Titular / Coacreditado" />
          {esOtroTViv && <Txt label="Especifique" value={sc[N('tipo_vivienda_otro')]} onChange={v => set(N('tipo_vivienda_otro'), v)} />}
        </div>
        <div style={st.field}>
          <label style={st.label}>Tiempo en la Población (años)</label>
          <input type="number" min="0" value={sc[N('tiempo_poblacion_anos')] ?? ''} onChange={e => set(N('tiempo_poblacion_anos'), e.target.value === '' ? null : Number(e.target.value))} style={st.input} />
        </div>
        {numDependientes !== undefined && (
          <div style={st.field}>
            <label style={st.label}>Núm. Dependientes Económicos</label>
            <input type="number" min="0" readOnly value={numDependientes ?? ''} style={{ ...st.input, background: '#f9fafb', color: '#6b7280' }} title="Se edita en la pestaña Titular" />
          </div>
        )}
      </div>

      {conGrupoVulnerabilidad && (
        <div style={{ marginBottom: 16 }}>
          <label style={st.label}>Grupo en situación de vulnerabilidad</label>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
            {[['SI','Sí'],['NO','No'],['NO_RESPONDE','Prefiero no responder']].map(([val, lbl]) => (
              <label key={val} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" name={`${prefix}_grupo_vuln`} checked={sc[N('grupo_vulnerabilidad')] === val} onChange={() => set(N('grupo_vulnerabilidad'), val)} />
                {lbl}
              </label>
            ))}
          </div>
          {sc[N('grupo_vulnerabilidad')] === 'SI' && (
            <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
              <Sel label="Grupo" value={sc[N('grupo_vulnerabilidad_id')]} onChange={v => set(N('grupo_vulnerabilidad_id'), v)} options={cats.gruposVulnerabilidad} />
            </div>
          )}
        </div>
      )}

      {/* PPE */}
      <div style={{ marginBottom: 12 }}>
        <label style={st.label}>¿Es o fue Persona Políticamente Expuesta?</label>
        <SiNo name={`${prefix}_ppe`} value={sc[N('ppe')]} onChange={v => set(N('ppe'), v)} />
        {sc[N('ppe')] && (
          <div style={st.ppeBox}>
            <PPESeccion prefix={`${prefix}_ppe`} sc={sc} set={set} />
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={st.label}>¿Parentesco con Persona Políticamente Expuesta?</label>
        <SiNo name={`${prefix}_parentesco_ppe`} value={sc[N('parentesco_ppe')]} onChange={v => set(N('parentesco_ppe'), v)} />
        {sc[N('parentesco_ppe')] && (
          <div style={st.ppeBox}>
            <PPESeccion prefix={`${prefix}_parentesco_ppe`} sc={sc} set={set} />
          </div>
        )}
      </div>
    </div>
  );
};

const InfoTrabajo = ({ prefix, label, sc, set, cats, conActividadFiscal = false }) => {
  const { isMobile, isTablet } = useResponsive();
  const grid3 = { display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, 'repeat(3, 1fr)'), gap: 16, marginBottom: 16 };
  const N = (field) => `${prefix}_${field}`;
  const actTrib = cats.actividadesTributarias.find(e => e.id === sc[N('actividad_tributaria_id')]);
  const esOtraAt = actTrib && actTrib.nombre.toLowerCase().includes('otro');
  const ingFijo  = Number(sc[N('ingreso_fijo')]) || 0;
  const ingOtros = Number(sc[N('otros_ingresos')]) || 0;
  const total    = ingFijo + ingOtros;

  const [coloniasFiscal, setColoniasFiscal] = useState([]);

  const buscarColoniasFiscal = async (cp) => {
    if (cp.length !== 5) { setColoniasFiscal([]); return; }
    try {
      const r = await fetch(`/api/sepomex/cp/${cp}`);
      const d = await r.json();
      if (d && d.length > 0) {
        setColoniasFiscal(d);
        set(N('fiscal_alcaldia'), d[0].municipio || '');
        set(N('fiscal_ciudad'), d[0].municipio || '');
        set(N('fiscal_estado'), d[0].estado || '');
      } else {
        setColoniasFiscal([]);
        alert('Código postal no encontrado en SEPOMEX');
      }
    } catch (err) {
      console.error('Error buscando código postal:', err);
    }
  };

  const setFiscalCp = (valor) => {
    const cp = valor.replace(/\D/g, '').slice(0, 5);
    set(N('fiscal_cp'), cp);
  };

  // Recarga las opciones de colonia (p.ej. al abrir un registro ya guardado,
  // donde el <select> aún no tiene el listado y mostraría la colonia vacía).
  useEffect(() => {
    buscarColoniasFiscal(sc[N('fiscal_cp')] || '');
  }, [sc[N('fiscal_cp')]]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={st.card}>
      <div style={st.title}>Información de Trabajo del {label}</div>

      <div style={grid3}>
        <Sel label="Sector Laboral" value={sc[N('sector_laboral_id')]} onChange={v => set(N('sector_laboral_id'), v)} options={cats.sectoresLaborales} />
        <Sel label="Ocupación" value={sc[N('ocupacion_id')]} onChange={v => set(N('ocupacion_id'), v)} options={cats.ocupaciones} />
        {conActividadFiscal && (
          <div>
            <Sel label="Actividad Tributaria" value={sc[N('actividad_tributaria_id')]} onChange={v => set(N('actividad_tributaria_id'), v)} options={cats.actividadesTributarias} />
            {esOtraAt && <Txt label="Especifique" value={sc[N('actividad_tributaria_otra')]} onChange={v => set(N('actividad_tributaria_otra'), v)} />}
          </div>
        )}
      </div>

      {conActividadFiscal && (
        <>
          <div style={{ ...st.subtitle, marginTop: 12 }}>Domicilio Fiscal</div>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1.5fr 0.25fr 0.25fr 0.3fr 1.4fr'), gap: 16, marginBottom: 16 }}>
            <Txt label="Calle" value={sc[N('fiscal_calle')]} onChange={v => set(N('fiscal_calle'), v)} />
            <Txt label="Núm. Ext." value={sc[N('fiscal_num_ext')]} onChange={v => set(N('fiscal_num_ext'), v)} />
            <Txt label="Núm. Int." value={sc[N('fiscal_num_int')]} onChange={v => set(N('fiscal_num_int'), v)} />
            <div style={st.field}>
              <label style={st.label}>C.P.</label>
              <input value={sc[N('fiscal_cp')] || ''} maxLength={5} onChange={e => setFiscalCp(e.target.value)} style={{ ...st.input, minWidth: 72, padding: '10px 6px', textAlign: 'center' }} placeholder="5 dígitos" />
            </div>
            <div style={st.field}>
              <label style={st.label}>Colonia</label>
              <select value={sc[N('fiscal_colonia')] || ''} onChange={e => set(N('fiscal_colonia'), e.target.value)} style={st.input}>
                <option value="">— Selecciona —</option>
                {coloniasFiscal.map((c, i) => <option key={i} value={c.colonia}>{c.colonia}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1fr 1fr 0.6fr 0.5fr 0.5fr'), gap: 16, marginBottom: 16 }}>
            <div style={st.field}>
              <label style={st.label}>Alcaldía / Municipio</label>
              <input value={sc[N('fiscal_alcaldia')] || ''} readOnly style={st.inputReadonly} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Ciudad</label>
              <input value={sc[N('fiscal_ciudad')] || ''} readOnly style={st.inputReadonly} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Estado</label>
              <input value={sc[N('fiscal_estado')] || ''} readOnly style={st.inputReadonly} />
            </div>
            <Txt label="País" value={sc[N('fiscal_pais')]} onChange={v => set(N('fiscal_pais'), v)} />
            <div />
          </div>
        </>
      )}

      <div style={grid3}>
        <Txt label="Giro de la Empresa" value={sc[N('giro_empresa')]} onChange={v => set(N('giro_empresa'), v)} />
        <Txt label="Puesto / Cargo" value={sc[N('puesto')]} onChange={v => set(N('puesto'), v)} />
        <Sel label="Tipo de Contrato Laboral" value={sc[N('tipo_contrato_id')]} onChange={v => set(N('tipo_contrato_id'), v)} options={cats.tiposContrato} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '0.7fr 0.7fr 1fr 0.7fr 0.9fr'), gap: 16, marginBottom: 16 }}>
        <Money label="Ingreso Fijo Comprobable" decimales={0} value={sc[N('ingreso_fijo')]} onChange={v => set(N('ingreso_fijo'), v)} />
        <Money label="Otros Ingresos" decimales={0} value={sc[N('otros_ingresos')]} onChange={v => set(N('otros_ingresos'), v)} />
        <Txt label="Fuente de Otros Ingresos" value={sc[N('otros_ingresos_fuente')]} onChange={v => set(N('otros_ingresos_fuente'), v)} />
        <div style={st.field}>
          <label style={st.label}>Total Ingresos</label>
          <div style={{ ...st.moneyWrap }}>
            <span style={st.moneySign}>$</span>
            <input readOnly value={total > 0 ? total.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : ''} style={{ ...st.moneyInput, background: '#f9fafb', color: '#6b7280' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const SeccionMedica = ({ prefix, titulo, sc, set }) => {
  const N = (f) => `${prefix}_${f}`;
  return (
    <div style={st.card}>
      <div style={st.title}>{titulo}</div>
      <MedRow num="1" label="¿Ha presentado variaciones de presión arterial?" campo={N('med_1_presion')} sc={sc} set={set} defaultNo />
      <MedRow num="2" label="¿Ha tenido alguna afección cardíaca?" campo={N('med_2_cardiaca')} sc={sc} set={set} defaultNo />
      <MedRow num="3" label="¿Padece o ha padecido enfermedades del hígado, riñones, vesícula o páncreas?" campo={N('med_3_organos')} sc={sc} set={set} defaultNo />
      <MedRow num="4" label="¿Padece o ha padecido cáncer o tumores?" campo={N('med_4_cancer')} sc={sc} set={set} defaultNo />
      <MedRow num="5" label="¿Padece Diabetes Mellitus?" campo={N('med_5_diabetes')} sc={sc} set={set} defaultNo />
    </div>
  );
};

const BancoScotiabank = ({ prospectoId, formData, setFormData }) => {
  const API = '/api';
  const { isMobile, isTablet } = useResponsive();
  const grid2 = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16, marginBottom: 16 };
  const grid3 = { display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, 'repeat(3, 1fr)'), gap: 16, marginBottom: 16 };
  const grid4 = { display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, 'repeat(4, 1fr)'), gap: 16, marginBottom: 16 };

  const [nacionalidades,          setNacionalidades]          = useState([]);
  const [escolaridades,           setEscolaridades]           = useState([]);
  const [estadosCiviles,          setEstadosCiviles]          = useState([]);
  const [regimenMatrimonial,      setRegimenMatrimonial]      = useState([]);
  const [tiposVivienda,           setTiposVivienda]           = useState([]);
  const [gruposVulnerabilidad,    setGruposVulnerabilidad]    = useState([]);
  const [parentescos,             setParentescos]             = useState([]);
  const [sectoresLaborales,       setSectoresLaborales]       = useState([]);
  const [ocupaciones,             setOcupaciones]             = useState([]);
  const [actividadesTributarias,  setActividadesTributarias]  = useState([]);
  const [tiposContrato,           setTiposContrato]           = useState([]);
  const [productosDestino,        setProductosDestino]        = useState([]);
  const [esquemasFinanciamiento,  setEsquemasFinanciamiento]  = useState([]);
  const [plazos,                  setPlazos]                  = useState([]);
  const [tiposPropiedad,          setTiposPropiedad]          = useState([]);
  const [usosVivienda,            setUsosVivienda]            = useState([]);
  const [origenesEnganche,        setOrigenesEnganche]        = useState([]);

  const [sc, setSc] = useState({ titular_fiscal_pais: 'México' });
  const [scotiaCargado, setScotiaCargado] = useState(false);

  const cats = {
    nacionalidades, escolaridades, estadosCiviles, regimenMatrimonial,
    tiposVivienda, gruposVulnerabilidad, parentescos, sectoresLaborales,
    ocupaciones, actividadesTributarias, tiposContrato,
  };

  useEffect(() => { cargarCatalogos(); }, []);

  const fmtDateInput = (v) => {
    if (!v) return null;
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    try { return new Date(s).toISOString().split('T')[0]; } catch { return null; }
  };

  useEffect(() => {
    if (!prospectoId) { setScotiaCargado(true); return; }
    fetch(`${API}/scotiabank/${prospectoId}`)
      .then(r => r.ok ? r.json() : null)
      .then(datos => {
        if (datos && datos.id) {
          ['titular_vigencia_identificacion','coac_vigencia_identificacion','tercero_fecha_nacimiento'].forEach(f => {
            datos[f] = fmtDateInput(datos[f]);
          });
          setSc(datos);
          setFormData(prev => ({ ...prev, scotiabank: datos }));
        }
      })
      .catch(e => console.error('Error cargando datos Scotiabank:', e))
      .finally(() => setScotiaCargado(true));
  }, [prospectoId]);

  // Estado Civil, Régimen Matrimonial, Identificación y Tipo de Vivienda
  // (Titular y Coacreditado) se derivan del módulo Titular / Coacreditado. Se
  // recalculan cuando cambian ahí, y también una vez que terminó de cargar lo
  // guardado de Scotiabank, para no quedar pisados por un valor anterior
  // guardado en la base de datos.
  useEffect(() => {
    if (!scotiaCargado) return;
    if (estadosCiviles.length === 0 || regimenMatrimonial.length === 0 || tiposVivienda.length === 0 || nacionalidades.length === 0) return;

    const derivar = (persona) => {
      const ecCentral = persona?.estado_civil;
      const ecNombre = SCOTIA_ESTADO_CIVIL_MAP[ecCentral] || null;
      const regNombre = ecCentral === 'Casado' ? (SCOTIA_REGIMEN_MAP[persona?.regimen_matrimonio] || null) : null;
      return {
        ecId: idPorNombreScotia(estadosCiviles, ecNombre),
        ecOtro: ecNombre === 'Otro Especifique' ? ecCentral : '',
        regId: idPorNombreScotia(regimenMatrimonial, regNombre),
        tvId: idPorNombreScotia(tiposVivienda, SCOTIA_TIPO_VIVIENDA_MAP[persona?.vive_en_casa] || null),
        noId: persona?.numero_identificacion || '',
        tipoId: persona?.tipo_identificacion || '',
        vigenciaId: persona?.fecha_vigencia_identificacion || '',
        nacId: idPorNombreScotia(nacionalidades, esMexicana(persona?.nacionalidad) ? 'Mexicana' : 'Extranjera'),
      };
    };
    const tit = derivar(formData.titular);
    const coac = derivar(formData.coacreditado);

    setSc(prev => {
      const next = { ...prev };
      let changed = false;
      const set = (k, v) => { if (next[k] !== v) { next[k] = v; changed = true; } };
      set('titular_estado_civil_id', tit.ecId);
      set('titular_estado_civil_otro', tit.ecOtro);
      set('titular_regimen_matrimonial_id', tit.regId);
      set('titular_tipo_vivienda_id', tit.tvId);
      set('titular_no_identificacion', tit.noId);
      set('titular_tipo_identificacion', tit.tipoId);
      set('titular_vigencia_identificacion', tit.vigenciaId);
      set('titular_nacionalidad_id', tit.nacId);
      set('coac_estado_civil_id', coac.ecId);
      set('coac_estado_civil_otro', coac.ecOtro);
      set('coac_regimen_matrimonial_id', coac.regId);
      set('coac_tipo_vivienda_id', coac.tvId);
      set('coac_no_identificacion', coac.noId);
      set('coac_tipo_identificacion', coac.tipoId);
      set('coac_vigencia_identificacion', coac.vigenciaId);
      set('coac_nacionalidad_id', coac.nacId);
      return changed ? next : prev;
    });
  }, [
    scotiaCargado, estadosCiviles, regimenMatrimonial, tiposVivienda, nacionalidades,
    formData.titular.estado_civil, formData.titular.regimen_matrimonio, formData.titular.vive_en_casa,
    formData.titular.numero_identificacion, formData.titular.tipo_identificacion, formData.titular.fecha_vigencia_identificacion,
    formData.titular.nacionalidad,
    formData.coacreditado?.estado_civil, formData.coacreditado?.regimen_matrimonio, formData.coacreditado?.vive_en_casa,
    formData.coacreditado?.numero_identificacion, formData.coacreditado?.tipo_identificacion, formData.coacreditado?.fecha_vigencia_identificacion,
    formData.coacreditado?.nacionalidad,
  ]);

  const cargarCatalogos = async () => {
    try {
      const names = [
        'cat_scotiabank_nacionalidad','cat_scotiabank_escolaridad','cat_scotiabank_estado_civil',
        'cat_scotiabank_regimen_matrimonial','cat_scotiabank_tipo_vivienda','cat_scotiabank_grupo_vulnerabilidad',
        'cat_scotiabank_parentesco_coacreditado','cat_scotiabank_sector_laboral','cat_scotiabank_ocupacion',
        'cat_scotiabank_actividad_tributaria','cat_scotiabank_tipo_contrato','cat_scotiabank_producto_destino',
        'cat_scotiabank_esquema_financiamiento','cat_scotiabank_plazo_credito','cat_scotiabank_tipo_propiedad',
        'cat_scotiabank_uso_vivienda','cat_scotiabank_origen_enganche',
      ];
      const results = await Promise.all(names.map(n => fetch(`${API}/catalogos/${n}`).then(r => r.json())));
      setNacionalidades(results[0]);  setEscolaridades(results[1]);   setEstadosCiviles(results[2]);
      setRegimenMatrimonial(results[3]); setTiposVivienda(results[4]); setGruposVulnerabilidad(results[5]);
      setParentescos(results[6]);     setSectoresLaborales(results[7]); setOcupaciones(results[8]);
      setActividadesTributarias(results[9]); setTiposContrato(results[10]); setProductosDestino(results[11]);
      setEsquemasFinanciamiento(results[12]); setPlazos(results[13]); setTiposPropiedad(results[14]);
      setUsosVivienda(results[15]);   setOrigenesEnganche(results[16]);
    } catch (e) { console.error('Error cargando catálogos Scotiabank:', e); }
  };

  const set = (campo, valor) => {
    setSc(prev => ({ ...prev, [campo]: valor }));
    setFormData(prev => ({ ...prev, scotiabank: { ...(prev.scotiabank || {}), [campo]: valor } }));
  };

  const guardar = async () => {
    if (creditoExcedeInmueble(sc.monto_credito, sc.valor_inmueble)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
    try {
      const res = await fetch(`${API}/scotiabank/${prospectoId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sc),
      });
      if (res.ok) alert('Datos Scotiabank guardados correctamente');
      else alert('Error al guardar');
    } catch (e) { alert('Error de conexión'); }
  };

  const montoCred = Number(sc.monto_credito) || 0;
  const valorInm  = Number(sc.valor_inmueble) || 0;
  const pctFin    = valorInm > 0 ? ((montoCred / valorInm) * 100).toFixed(2) : '';

  const tieneCoac = !!formData.coacreditado?.nombres && formData.coacreditado?.participa_ingresos === true;

  return (
    <div>
      {/* ── Titular ── */}
      <InfoGeneral prefix="titular" label="Titular" sc={sc} set={set} cats={cats} conGrupoVulnerabilidad numDependientes={formData?.titular?.num_dependientes} />
      <InfoTrabajo prefix="titular" label="Titular" sc={sc} set={set} cats={cats} conActividadFiscal />

      {/* ── Coacreditado ── */}
      {tieneCoac && (
        <>
          <div style={st.card}>
            <div style={st.title}>Parentesco del Coacreditado</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ flex: isMobile ? '1 1 100%' : '0 1 300px' }}>
                <Sel label="Parentesco con el solicitante" value={sc.coac_parentesco_id} onChange={v => set('coac_parentesco_id', v)} options={parentescos} />
              </div>
              <div style={{ flex: isMobile ? '1 1 100%' : '0 1 300px' }}>
                <div style={st.field}>
                  <label style={st.label}>Es coacreditado o es Obligado Solidario</label>
                  <select value={sc.coac_tipo_participacion === 'obligado_solidario' ? 'obligado_solidario' : 'coacreditado'}
                    onChange={e => set('coac_tipo_participacion', e.target.value)} style={st.input}>
                    <option value="coacreditado">Coacreditado</option>
                    <option value="obligado_solidario">Obligado Solidario</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <InfoGeneral prefix="coac" label="Coacreditado" sc={sc} set={set} cats={cats} conGrupoVulnerabilidad />
          <InfoTrabajo prefix="coac" label="Coacreditado" sc={sc} set={set} cats={cats} conActividadFiscal={false} />
        </>
      )}

      {/* ── Antecedentes Médicos ── */}
      <SeccionMedica prefix="titular" titulo="Antecedentes Médicos del Titular" sc={sc} set={set} />
      {tieneCoac && (
        <SeccionMedica prefix="coac" titulo="Antecedentes Médicos del Coacreditado" sc={sc} set={set} />
      )}

      {/* ── Crédito ── */}
      <div style={st.card}>
        <div style={st.title}>Información del Crédito</div>

        <div style={grid2}>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>¿Oferta Vinculante?</label>
            <SiNo name="oferta_vinculante" value={sc.oferta_vinculante} onChange={v => set('oferta_vinculante', v)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>¿Consolidar Ingresos?</label>
            <SiNo name="coac_consolidar_ingresos_cred" value={sc.coac_consolidar_ingresos} onChange={v => set('coac_consolidar_ingresos', v)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1fr 0.4fr 0.6fr 0.5fr 1fr'), gap: 16, marginBottom: 16 }}>
          <Sel label="Producto / Destino" value={sc.producto_destino_id} onChange={v => set('producto_destino_id', v)} options={productosDestino} />
          <div>
            <label style={st.label}>Apoyo Infonavit</label>
            <SiNo name="apoyo_infonavit" value={sc.apoyo_infonavit} onChange={v => set('apoyo_infonavit', v)} />
          </div>
          <Sel label="Esquema de Financiamiento" value={sc.esquema_financiamiento_id} onChange={v => set('esquema_financiamiento_id', v)} options={esquemasFinanciamiento} />
          <Sel label="Plazo" value={sc.plazo_id} onChange={v => set('plazo_id', v)} options={plazos} />
          <div />
        </div>

        <div style={grid4}>
          <div>
            <label style={st.label}>¿Comisión de Contratación?</label>
            <SiNo name="comision_contratacion" value={sc.comision_contratacion} onChange={v => set('comision_contratacion', v)} />
          </div>
          <div>
            <label style={st.label}>¿Penalización por Pago Anticipado?</label>
            <SiNo name="penalizacion_pago_anticipado" value={sc.penalizacion_pago_anticipado} onChange={v => set('penalizacion_pago_anticipado', v)} />
          </div>
          <Sel label="Uso de la Vivienda" value={sc.uso_vivienda_id} onChange={v => set('uso_vivienda_id', v)} options={usosVivienda} />
          <Sel label="Tipo de Propiedad" value={sc.tipo_propiedad_id} onChange={v => set('tipo_propiedad_id', v)} options={tiposPropiedad} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '0.6fr 1fr 0.6fr 0.6fr 0.6fr 0.6fr'), gap: 16, marginBottom: 16 }}>
          <Money label="Monto del Crédito" decimales={0} value={sc.monto_credito} onChange={v => set('monto_credito', v)} />
          <Money label="Valor Aprox. del Inmueble" decimales={0} value={sc.valor_inmueble} onChange={v => set('valor_inmueble', v)} />
          <div style={st.field}>
            <label style={st.label}>% Financiamiento</label>
            <input readOnly value={pctFin ? pctFin + '%' : ''} style={{ ...st.input, background: '#f9fafb', color: '#6b7280' }} />
          </div>
          <Money label="Monto del Enganche" decimales={0} value={sc.monto_enganche} onChange={v => set('monto_enganche', v)} />
          <Sel label="Origen del Enganche" value={sc.origen_enganche_id} onChange={v => set('origen_enganche_id', v)} options={origenesEnganche} />
        </div>
        {sc.origen_enganche_id === 4 && (
          <div style={{ maxWidth: isMobile ? '100%' : 400 }}>
            <Txt label="Especificar origen (Otro)" value={sc.origen_enganche_otro_especificar} onChange={v => set('origen_enganche_otro_especificar', v)} />
          </div>
        )}
        {sc.origen_enganche_id === 5 && (
          <div style={{ maxWidth: isMobile ? '100%' : 400 }}>
            <Txt label="Especificar (No aplica)" value={sc.origen_enganche_no_aplica_especificar} onChange={v => set('origen_enganche_no_aplica_especificar', v)} />
          </div>
        )}

        <div style={{ ...st.subtitle, marginTop: 16 }}>Cuenta para Manejo de Crédito</div>
        <div style={grid4}>
          <Txt label="Número de Cuenta de Cheques" value={sc.num_cuenta_cheques} onChange={v => set('num_cuenta_cheques', v)} />
          <div style={st.field}>
            <label style={st.label}>Servicios</label>
            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              {[['servicio_nomina','Nómina'],['servicio_servicios','Servicios'],['servicio_ninguno','Ninguno']].map(([k,lbl]) => (
                <label key={k} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!sc[k]} onChange={e => set(k, e.target.checked)} />
                  {lbl}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label style={st.label}>¿Tramitar Tarjeta Scotiabank?</label>
            <SiNo name="desea_tarjeta_credito" value={sc.desea_tarjeta_credito} onChange={v => set('desea_tarjeta_credito', v)} />
          </div>
          <Txt label="Antigüedad como cliente (años)" value={sc.antiguedad_cliente_anos} onChange={v => set('antiguedad_cliente_anos', v)} />
        </div>

        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <label style={st.label}>Personalidad Jurídica</label>
          <SiNo name="personalidad_juridica" value={sc.personalidad_juridica} onChange={v => set('personalidad_juridica', v)} />
        </div>

        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <label style={st.label}>¿Pagos de Tercero?</label>
          <SiNo name="pagos_tercero" value={sc.pagos_tercero} onChange={v => set('pagos_tercero', v)} />
        </div>

        {sc.pagos_tercero && (
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: 16 }}>
            <div style={{ ...st.subtitle, marginTop: 0 }}>Datos del Tercero</div>
            <div style={grid3}>
              <Txt label="Nombre / Razón Social" value={sc.tercero_nombre} onChange={v => set('tercero_nombre', v)} />
              <Txt label="Dirección" value={sc.tercero_direccion} onChange={v => set('tercero_direccion', v)} />
              <Txt label="Relación con el Solicitante" value={sc.tercero_relacion} onChange={v => set('tercero_relacion', v)} />
            </div>
            <div style={grid4}>
              <Txt label="RFC" value={sc.tercero_rfc} onChange={v => set('tercero_rfc', v)} />
              <Txt label="Negocio" value={sc.tercero_negocio} onChange={v => set('tercero_negocio', v)} />
              <FechaInput label="Fecha Nacimiento / Constitución" value={sc.tercero_fecha_nacimiento} onChange={v => set('tercero_fecha_nacimiento', v)} />
              <Txt label="Lugar de Constitución" value={sc.tercero_lugar_constitucion} onChange={v => set('tercero_lugar_constitucion', v)} />
            </div>
            <div style={{ maxWidth: isMobile ? '100%' : 300 }}>
              <Txt label="Nacionalidad" value={sc.tercero_nacionalidad} onChange={v => set('tercero_nacionalidad', v)} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={guardar} style={st.saveBtn}>Guardar Datos Scotiabank</button>
      </div>
    </div>
  );
};

export default BancoScotiabank;
